import {
  AlertTriangle,
  CheckCircle2,
  MessageSquare,
  XCircle,
} from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { SmsStatusResponse } from "../../types/doctorAdmin.definition";

interface SmsStatusPanelProps {
  status: SmsStatusResponse | null;
  loading: boolean;
  error: string | null;
}

// MED-1254: SMS notification diagnostics panel — shows whether SMS is
// enabled for the doctor, whether they've given consent, and whether their
// phone is verified. When SMS is disabled, consent/phone-verified aren't
// shown as "the problem" since SMS itself is simply off. When SMS is
// enabled but the phone isn't verified, that's flagged as the likely reason
// SMS notifications aren't reaching the doctor.
export const SmsStatusPanel: React.FC<SmsStatusPanelProps> = ({
  status,
  loading,
  error,
}) => {
  const { t } = useTranslation();

  return (
    <div className="page-section card-container">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("admin.doctors.sms.title")}
        </h3>
        <MessageSquare className="w-5 h-5 text-gray-400" />
      </div>

      {loading && (
        <p className="text-sm text-gray-600">{t("common.loading")}</p>
      )}

      {!loading && error && (
        <p className="text-sm text-red-600">{t("admin.doctors.sms.error")}</p>
      )}

      {!loading && !error && status && !status.enabled && (
        <div className="flex items-center py-4 text-gray-600">
          <XCircle className="flex-shrink-0 mr-3 w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {t("admin.doctors.sms.disabled")}
            </p>
            <p className="text-sm text-gray-600">
              {t("admin.doctors.sms.disabledDescription")}
            </p>
          </div>
        </div>
      )}

      {!loading && !error && status && status.enabled && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs text-neutral-500">
              {t("admin.doctors.sms.consent")}
            </p>
            <p className="flex items-center mt-1 text-sm font-semibold text-neutral-900">
              {status.consent === null && (
                <span>{t("admin.doctors.sms.consentNotAsked")}</span>
              )}
              {status.consent === true && (
                <>
                  <CheckCircle2 className="flex-shrink-0 mr-1.5 w-4 h-4 text-green-600" />
                  <span>{t("admin.doctors.sms.consentGiven")}</span>
                </>
              )}
              {status.consent === false && (
                <>
                  <XCircle className="flex-shrink-0 mr-1.5 w-4 h-4 text-red-600" />
                  <span>{t("admin.doctors.sms.consentDenied")}</span>
                </>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-neutral-500">
              {t("admin.doctors.sms.phoneVerified")}
            </p>
            <p className="flex items-center mt-1 text-sm font-semibold text-neutral-900">
              {status.phoneVerified ? (
                <>
                  <CheckCircle2 className="flex-shrink-0 mr-1.5 w-4 h-4 text-green-600" />
                  <span>{t("admin.doctors.sms.phoneVerifiedYes")}</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="flex-shrink-0 mr-1.5 w-4 h-4 text-amber-600" />
                  <span>{t("admin.doctors.sms.phoneVerifiedNo")}</span>
                </>
              )}
            </p>
          </div>

          {!status.phoneVerified && (
            <div className="flex items-start p-3 rounded-md border sm:col-span-2 bg-amber-50 border-amber-200">
              <AlertTriangle className="flex-shrink-0 mt-0.5 mr-2 w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-800">
                {t("admin.doctors.sms.phoneVerifiedWarning")}
              </p>
            </div>
          )}
        </div>
      )}

      {!loading && !error && !status && (
        <p className="text-sm text-gray-600">
          {t("admin.doctors.sms.unavailable")}
        </p>
      )}
    </div>
  );
};
